import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { api } from "../../../api/apiService";
import { Row, Col, InputGroup, Form, Button, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

const MonthlyReport = () => {
  const company = localStorage.getItem("company");

  const [htmlContent, setHtmlContent] = useState(null);
  const [csvData, setCsvData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const navigate = useNavigate();

  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const fetchMonthlyReport = async (month, year) => {
    setIsLoading(true);
    setHtmlContent(null);
    setError(null);

    try {
      const response = await api.get(
        `/employee/report-monthly?month=${month}&year=${year}`
      );
      setHtmlContent(response.data.html);
      setCsvData(response.data.html);
    } catch (error) {
      console.error("Error fetching attendance report:", error);
      toast.error("Failed to fetch attendance report");
      setError("Failed to fetch attendance report.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMonthlyReport(selectedMonth, selectedYear);
  }, [selectedMonth, selectedYear]);

  const handlePrint = () => {
    if (!htmlContent) {
      toast.error("No report data available to print.");
      return;
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    doc
      .querySelectorAll(".total_col-1, .total_col_1")
      .forEach((el) => el.remove());
    const updatedHtmlContent = doc.documentElement.innerHTML;
    const printWindow = window.open();

    printWindow.document.write(`
    <html>
      <head>
        <title>Monthly Overall - ${
          months.find((m) => m.value === selectedMonth)?.label
        } ${selectedYear}</title>
        <style>
          tr {
            page-break-inside: avoid;
          }

          @page {
            size: 26in 17in;
          }

          .attendance-report table {
            width: 100%;
            table-layout: auto;
          }

          /* Ensure only the last total column is visible in print */
          @media print {
            .attendance-report table .total_col-2,
            .attendance-report table .total_col_2 {
              display: table-cell !important;
            }

            header, footer {
              display: none; /* Hide header/footer when printing */
            }
          }
        </style>
      </head>
      <body>
        <div style="padding:20px 30px;">
          ${updatedHtmlContent} <!-- Use the modified HTML content -->
          <h3 style="margin-top: 3rem;">${company}</h3>
        </div>
      </body>
    </html>
  `);

    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.addEventListener("afterprint", () => {
          printWindow.close();
        });
      }, 500);
    };
  };

  useEffect(() => {
    if (htmlContent) {
      document
        .querySelectorAll(
          ".attendance-report table .total_col-1, .attendance-report table .total_col_1"
        )
        .forEach((cell) => (cell.style.display = "table-cell"));

      document
        .querySelectorAll(
          ".attendance-report table .total_col-2, .attendance-report table .total_col_2"
        )
        .forEach((cell) => cell.remove());
    }
  }, [htmlContent]);

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 10; i <= currentYear; i++) {
      years.push(i);
    }
    return years;
  };

  const handleExportCsv = () => {
    if (!htmlContent) {
      toast.error("No data available to export.");
      return;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, "text/html");
      const tables = doc.querySelectorAll("table");

      if (!tables.length) {
        toast.error("No tables found in the report.");
        return;
      }

      let csvContent = "";

      tables.forEach((table, tableIndex) => {
        if (tableIndex > 0) {
          csvContent += "\n\n";
        }

        // Add ID#, Name, Total headers first
        csvContent += '"Name","Total",';
        const secondTr = table.querySelector("thead tr:nth-of-type(2)");
        const dayHeaders = Array.from(secondTr.querySelectorAll("th"));

        const firstDate = parseInt(dayHeaders[0]?.innerText.trim(), 10);

        if (!firstDate) {
          console.error("First date not found!");
          return;
        }
        // date
        const daysCount = dayHeaders.length;
        const dayNumbers = Array.from(
          { length: daysCount },
          (_, i) => firstDate + i
        );
        csvContent += dayNumbers.join(",") + "\n";
        // days
        csvContent += '"","",';
        const dayNames = Array.from(
          table.querySelectorAll("thead tr:last-child th")
        )
          .map((th) => `"${th.innerText.trim().replace(/"/g, '""')}"`)
          .join(",");
        csvContent += dayNames + "\n";

        const bodyRows = Array.from(table.querySelectorAll("tbody tr"));
        bodyRows.forEach((row, rowIndex) => {
          const isLastRow = rowIndex === bodyRows.length - 1;
          const cells = Array.from(row.querySelectorAll("td, th"));
          const rowContent = [];
          cells.slice(0, -1).forEach((cell) => {
            const content = `"${cell.innerText.trim().replace(/"/g, '""')}"`;
            rowContent.push(content);
          });

          csvContent += rowContent.join(",") + "\n";
        });

        const footerRows = Array.from(table.querySelectorAll("tfoot tr"));
        if (footerRows.length) {
          footerRows.forEach((row) => {
            const cells = Array.from(row.querySelectorAll("td, th"));
            const rowContent = [];

            cells.slice(0, -1).forEach((cell, cellIndex) => {
              if (cell.hasAttribute("colspan") && cellIndex === 0) {
                const colspan = parseInt(cell.getAttribute("colspan"));
                for (let i = 1; i < colspan - 1; i++) {
                  rowContent.push('""');
                }
              }

              const content = `"${cell.innerText.trim().replace(/"/g, '""')}"`;
              rowContent.push(content);
            });

            csvContent += rowContent.join(",") + "\n";
          });
        }
      });

      // Create and trigger download
      const blob = new Blob(["\ufeff" + csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Monthly_Report_${months[selectedMonth - 1].label}_${selectedYear}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating CSV:", error);
    }
  };

  const Payroll = () => {
    if (selectedMonth && selectedYear) {
      navigate(`make-payroll?month=${selectedMonth}&year=${selectedYear}`);
    } else {
      toast.error("Please select a valid month and year.");
    }
  };

  return (
    <>
      <main id="main" className="main">
        <div className="pagetitle d-flex justify-content-between align-items-center mb-4">
          <h1>Monthly Summary</h1>
        </div>

        <section className="section">
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  <Row className="mt-3 g-2">
                    <Col xs={12} md={2}>
                      <InputGroup>
                        <Form.Select
                          value={selectedMonth}
                          onChange={(e) =>
                            setSelectedMonth(parseInt(e.target.value))
                          }
                        >
                          {months.map((month) => (
                            <option key={month.value} value={month.value}>
                              {month.label}
                            </option>
                          ))}
                        </Form.Select>
                      </InputGroup>
                    </Col>
                    <Col xs={12} md={2}>
                      <InputGroup>
                        <Form.Select
                          value={selectedYear}
                          onChange={(e) =>
                            setSelectedYear(parseInt(e.target.value))
                          }
                        >
                          {generateYearOptions().map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </Form.Select>
                      </InputGroup>
                    </Col>
                    <Col xs={12} md={3}>
                      <Button
                        onClick={() =>
                          fetchMonthlyReport(selectedMonth, selectedYear)
                        }
                        variant="primary"
                        disabled={isLoading}
                      >
                        Search
                      </Button>
                    </Col>
                    <Col xs={12} md={5} className="text-md-end pe-0">
                      <Button
                        className="me-2 mb-1"
                        onClick={Payroll}
                        variant="primary"
                        disabled={isLoading || !htmlContent}
                      >
                        Make Payroll
                      </Button>
                      <Button
                        className="me-2 mb-1"
                        onClick={handleExportCsv}
                        variant="secondary"
                        disabled={isLoading || !csvData}
                      >
                        Export
                      </Button>
                      <Button
                        onClick={handlePrint}
                        variant="primary"
                        disabled={isLoading || !htmlContent}
                      >
                        Print Report
                      </Button>
                    </Col>
                  </Row>

                  {isLoading && (
                    <div className="text-center mt-3">
                      <Spinner animation="border" variant="primary" />
                      <span className="ms-2">Loading...</span>
                    </div>
                  )}

                  {error && (
                    <div className="alert alert-danger mt-3" role="alert">
                      {error}
                    </div>
                  )}

                  {htmlContent && !isLoading && (
                    <div
                      className="attendance-report mt-3"
                      dangerouslySetInnerHTML={{ __html: htmlContent }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default MonthlyReport;
